import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Edit, Trash2, Box, Router, Shield, Globe, Key, Cloud,
  Wifi, Server, HardDrive, Phone, FileText, Lock, Building2, Clock,
  ExternalLink, Copy, Check, Calendar, Mail
} from 'lucide-react';
import { documentationAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import RelatedItems from '../../components/documentation/RelatedItems';
import Attachments from '../../components/documentation/Attachments';
import { isInternalRole } from '../../utils/roles.js';

// Icon mapping for asset types
const TYPE_ICONS = {
  'network-device': Router,
  'ssl-certificate': Shield,
  'domain': Globe,
  'software-license': Key,
  'cloud-service': Cloud,
  'lan': Wifi,
  'backup': HardDrive,
  'email': FileText,
  'active-directory': Server,
  'security': Shield,
  'voice-pbx': Phone,
  default: Box
};

function getTypeIcon(slug) {
  return TYPE_ICONS[slug] || TYPE_ICONS.default;
}

// Visibility badge
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal Only', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company Visible', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${className}`}>
      <Icon className="w-4 h-4" />
      {label}
    </span>
  );
}

// Copyable field
function CopyableField({ value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
      title="Copy"
    >
      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
    </button>
  );
}

// Dynamic field renderer
function FieldDisplay({ field, value }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-neutral-400 italic">Not set</span>;
  }

  switch (field.type) {
    case 'url':
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
        >
          {value}
          <ExternalLink className="w-3 h-3" />
        </a>
      );
    case 'email':
      return (
        <a
          href={`mailto:${value}`}
          className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
        >
          <Mail className="w-4 h-4" />
          {value}
        </a>
      );
    case 'checkbox':
      return (
        <span className={`px-2 py-0.5 rounded text-sm font-medium ${
          value ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    case 'date':
      return (
        <span className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-neutral-400" />
          {new Date(value).toLocaleDateString()}
        </span>
      );
    case 'number':
      return <span className="font-mono">{value}</span>;
    case 'textarea':
      return <p className="whitespace-pre-wrap">{value}</p>;
    case 'select':
      return <span className="px-2 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-sm">{value}</span>;
    default:
      return <span>{String(value)}</span>;
  }
}

export default function FlexibleAssetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isInternal = isInternalRole(user?.role);

  // Fetch asset
  const { data, isLoading, error } = useQuery({
    queryKey: ['documentation', 'flexible-asset', id],
    queryFn: () => documentationAPI.getFlexibleAsset(id),
  });

  const asset = data?.data?.asset;
  const assetType = asset?.asset_type;
  const fields = assetType?.field_schema || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => documentationAPI.deleteFlexibleAsset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'flexible-assets'] });
      navigate('/documentation/flexible-assets');
    },
  });

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="mb-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-64 animate-pulse" />
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2 mb-4 animate-pulse" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-16 bg-neutral-100 dark:bg-neutral-900 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="animate-fade-in">
        <div className="text-center py-12">
          <Box className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
          <h2 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">Asset Not Found</h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The flexible asset you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link
            to="/documentation/flexible-assets"
            className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Flexible Assets
          </Link>
        </div>
      </div>
    );
  }

  const TypeIcon = getTypeIcon(assetType?.slug);

  // Group fields into sections (fields with same 'section' property)
  const fieldsBySection = fields.reduce((acc, field) => {
    const section = field.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {});

  return (
    <div className="animate-fade-in">
      {/* Back link */}
      <Link
        to="/documentation/flexible-assets"
        className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Flexible Assets
      </Link>

      {/* Header */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 mb-6">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-neutral-100 dark:bg-neutral-700">
                <TypeIcon className="w-8 h-8 text-neutral-600 dark:text-neutral-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
                  {asset.name}
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                  {assetType && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300">
                      {assetType.name}
                    </span>
                  )}
                  {isInternal && <VisibilityBadge visibility={asset.visibility} />}
                </div>
              </div>
            </div>

            {/* Actions */}
            {isInternal && (
              <div className="flex items-center gap-2">
                <Link
                  to={`/documentation/flexible-assets?edit=${id}`}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit className="w-5 h-5" />
                </Link>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Quick metadata */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-neutral-500 dark:text-neutral-400">
            {asset.company_name && (
              <span className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {asset.company_name}
              </span>
            )}
            {asset.updated_at && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                Updated {new Date(asset.updated_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Field Values */}
        <div className="lg:col-span-2 space-y-6">
          {Object.entries(fieldsBySection).map(([section, sectionFields]) => (
            <div key={section} className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">{section}</h2>
              <div className="space-y-4">
                {sectionFields.map(field => {
                  const value = asset.field_values?.[field.name];
                  const shouldShowCopy = ['text', 'url', 'email', 'number'].includes(field.type) && value;

                  return (
                    <div key={field.name} className="flex items-start justify-between p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
                      <div className="flex-1">
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">{field.label}</p>
                        <div className="text-neutral-900 dark:text-white">
                          <FieldDisplay field={field} value={value} />
                        </div>
                      </div>
                      {shouldShowCopy && <CopyableField value={value} />}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Show message if no fields */}
          {fields.length === 0 && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">
                No custom fields defined for this asset type.
              </p>
            </div>
          )}

          {/* Related Items */}
          <RelatedItems
            sourceType="flexible_asset"
            sourceId={id}
            companyId={asset.company_id}
          />

          {/* Attachments */}
          <Attachments
            assetType="flexible_asset"
            assetId={id}
            companyId={asset.company_id}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info Card */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">Details</h3>
            <div className="space-y-3">
              {assetType && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Type</span>
                  <span className="text-neutral-900 dark:text-white font-medium">{assetType.name}</span>
                </div>
              )}
              {asset.company_name && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Company</span>
                  <span className="text-neutral-900 dark:text-white font-medium">{asset.company_name}</span>
                </div>
              )}
              {isInternal && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Visibility</span>
                  <span className="text-neutral-900 dark:text-white font-medium capitalize">{asset.visibility}</span>
                </div>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">Activity</h3>
            <div className="space-y-3 text-sm">
              {asset.created_at && (
                <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
                  <Clock className="w-4 h-4" />
                  Created {new Date(asset.created_at).toLocaleDateString()}
                </div>
              )}
              {asset.updated_at && (
                <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
                  <Clock className="w-4 h-4" />
                  Updated {new Date(asset.updated_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Delete Asset?
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              Are you sure you want to delete "{asset.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
